'use strict';

/**
 * Pict-Provider-MediaAPI
 * ----------------------
 * Browser-side client for Media — screenshots, photos, drawings, recordings,
 * how-to assets — owned polymorphically (OwnerType + IDOwner) with bytes in
 * Parime. Two-stage upload mirrors SessionAPI. Reads are filtered by owner
 * (team-visible; the server scopes by IDCustomer). Images/SVGs display straight
 * from the blob URL.
 *
 * An entity gallery uses OwnerType = the entity key ('Vision' | 'Feature' | …).
 * Images/drawings embedded in a document are owned by that document's entity
 * (the same OwnerType + IDOwner), so they also surface in the entity's gallery.
 */

const libPictProvider = require('pict').ServiceProviderBase;
const libFrameworkClient = require('./FrameworkClient.js');

const _DefaultProviderConfiguration =
{
	ProviderIdentifier: 'MediaAPI',
	Dependencies: [],
	RoutePrefix: '/1.0/'
};

class PictProviderMediaAPI extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		let tmpOptions = Object.assign({}, _DefaultProviderConfiguration, pOptions || {});
		super(pFable, tmpOptions, pServiceHash);
		this.routePrefix = this.options.RoutePrefix;
	}

	_url(pPath)
	{
		let tmpPrefix = this.routePrefix;
		if (tmpPrefix.charAt(tmpPrefix.length - 1) !== '/') tmpPrefix += '/';
		return tmpPrefix + pPath.replace(/^\//, '');
	}

	_guessType(pMime)
	{
		let tmpMime = pMime || '';
		if (tmpMime.indexOf('image/svg') === 0) return 'Drawing';
		if (tmpMime.indexOf('image/') === 0) return 'Screenshot';
		if (tmpMime.indexOf('video/') === 0 || tmpMime.indexOf('audio/') === 0) return 'Recording';
		return 'Other';
	}

	blobURL(pIDMedia) { return this._url('Media/' + pIDMedia + '/Blob'); }

	/**
	 * List media owned by (pOwnerType, pIDOwner). meadow-filter DSL chains
	 * FBV~Field~EQ~Value terms with ~ for AND.
	 */
	listForOwner(pOwnerType, pIDOwner)
	{
		let tmpFilter = 'FBV~OwnerType~EQ~' + pOwnerType + '~FBV~IDOwner~EQ~' + pIDOwner;
		return libFrameworkClient.getJSON(this.fable, this._url('Medias/FilteredTo/' + tmpFilter + '/0/250'))
			.then((pBody) => Array.isArray(pBody) ? pBody : (pBody && pBody.Records) || [])
			.catch(() => []);
	}

	/**
	 * Two-stage upload: POST metadata to /Media/New, then PUT the bytes to
	 * /Media/:id/Blob. pFile is a browser File/Blob (name/type/size) or an
	 * ArrayBuffer. pOverrides can set Title/MediaType/MimeType/OriginalFileName
	 * (used for SVG drawings, which arrive as a string-built Blob).
	 */
	upload(pOwnerType, pIDOwner, pFile, pOverrides)
	{
		let tmpOverrides = pOverrides || {};
		let tmpMime = tmpOverrides.MimeType || pFile.type || 'application/octet-stream';
		let tmpMeta =
		{
			OwnerType: pOwnerType,
			IDOwner: pIDOwner,
			Title: tmpOverrides.Title || pFile.name || 'Untitled',
			MediaType: tmpOverrides.MediaType || this._guessType(tmpMime),
			MimeType: tmpMime,
			OriginalFileName: tmpOverrides.OriginalFileName || pFile.name || 'file',
			FileSizeBytes: pFile.size || pFile.byteLength || 0,
			// Private uploads (the user gallery, note images) are owned by the uploader (Media.IDUser);
			// omitted/false leaves it team-visible like entity-gallery media.
			Private: !!tmpOverrides.Private
		};

		return libFrameworkClient.postJSON(this.fable, this._url('Media/New'), tmpMeta)
			.then((pBody) =>
			{
				if (!pBody || !pBody.Success || !pBody.Media)
				{
					throw new Error((pBody && pBody.Error) || 'Could not register media.');
				}
				let tmpID = pBody.Media.IDMedia;
				return this._putBlob(tmpID, pFile, tmpMime).then((pPutBody) => Object.assign({ IDMedia: tmpID }, pBody.Media, pPutBody));
			});
	}

	/**
	 * Re-point an existing Media row at a real owner record. Used when an image
	 * was uploaded from an entity CREATE form: the bytes went up before the record
	 * had an id (IDOwner 0), and once it is saved we re-own the media to its id so
	 * it surfaces in that entity's gallery. Generic meadow update (PUT /1.0/Media);
	 * the Media tenancy hook scopes it to the customer + team media (IDUser 0), so
	 * only the pending team uploads created here are reachable.
	 */
	reassignOwner(pIDMedia, pOwnerType, pIDOwner)
	{
		return libFrameworkClient.putJSON(this.fable, this._url('Media'),
			{ IDMedia: pIDMedia, OwnerType: pOwnerType, IDOwner: pIDOwner });
	}

	/** Replace the bytes of an existing Media row (e.g. re-saving a drawing). */
	updateBlob(pIDMedia, pFile, pMimeType)
	{
		let tmpMime = pMimeType || pFile.type || 'application/octet-stream';
		return this._putBlob(pIDMedia, pFile, tmpMime).then((pPutBody) => Object.assign({ IDMedia: pIDMedia }, pPutBody));
	}

	_putBlob(pIDMedia, pFile, pMimeType)
	{
		let tmpBlob = (pFile instanceof ArrayBuffer) ? new Blob([pFile], { type: pMimeType }) : pFile;
		// Through the shared RestClient's binary path, so a 401 on the blob PUT
		// drives session recovery + replays the upload (the request body Buffer is
		// re-sent on the replay).
		return libFrameworkClient.uploadBinary(this.fable, this._url('Media/' + pIDMedia + '/Blob'), tmpBlob, pMimeType, 'PUT');
	}

	/**
	 * Save an external URL reference without uploading binary bytes. The URL is stored in
	 * OriginalFileName; MediaType is forced to 'Link' and MimeType to 'text/x-uri' so the
	 * gallery can distinguish it from a real file and render an open-in-new-tab card.
	 */
	uploadURL(pOwnerType, pIDOwner, pURL, pTitle, pOverrides)
	{
		let tmpOverrides = pOverrides || {};
		let tmpMeta =
		{
			OwnerType: pOwnerType,
			IDOwner: pIDOwner,
			Title: String(pTitle || pURL || 'Link').slice(0, 200),
			MediaType: 'Link',
			MimeType: 'text/x-uri',
			OriginalFileName: String(pURL || '').slice(0, 256),
			FileSizeBytes: 0,
			Private: !!tmpOverrides.Private
		};
		return libFrameworkClient.postJSON(this.fable, this._url('Media/New'), tmpMeta)
			.then((pBody) =>
			{
				if (!pBody || !pBody.Success || !pBody.Media)
				{
					throw new Error((pBody && pBody.Error) || 'Could not register URL.');
				}
				return Object.assign({ IDMedia: pBody.Media.IDMedia }, pBody.Media);
			});
	}

	remove(pIDMedia)
	{
		return libFrameworkClient.delJSON(this.fable, this._url('Media/' + pIDMedia)).then(() => true);
	}

	/**
	 * The caller's PRIVATE media -- their personal gallery (Media.IDUser = them). Optionally narrow
	 * to one OwnerType (e.g. 'Note' for just note images). Team/entity media never comes back here.
	 */
	listMine(pOwnerType)
	{
		let tmpPath = 'Media/Mine' + (pOwnerType ? ('?OwnerType=' + encodeURIComponent(pOwnerType)) : '');
		return libFrameworkClient.getJSON(this.fable, this._url(tmpPath))
			.then((pBody) => Array.isArray(pBody) ? pBody : (pBody && pBody.Records) || [])
			.catch(() => []);
	}

	/** Delete one of the caller's own private media (server enforces IDUser ownership). */
	removeMine(pIDMedia)
	{
		return libFrameworkClient.delJSON(this.fable, this._url('Media/Mine/' + pIDMedia)).then(() => true);
	}
}

module.exports = PictProviderMediaAPI;
module.exports.default_configuration = _DefaultProviderConfiguration;
